import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  Paper,
  Stack,
  Typography,
  Grid,
  Button,
  Divider,
} from "@mui/material";
import { Link as RouterLink } from "react-router-dom";
import { useStyle } from "../../context/styleContext/styleContext";
import Seo from "../../components/Seo";

export default function About() {
  const { style } = useStyle();
  const [height, setHeight] = useState(window.innerHeight);
  const topRef = useRef(null);

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
    topRef.current?.scrollIntoView({ block: "start" });
  }, []);

  useEffect(() => {
    function handleResize() {
      setHeight(window.innerHeight);
    }

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const aboutSchema = {
    "@context": "https://schema.org",
    "@type": "AboutPage",
    name: "About Parish Audio",
    url: "https://parishaudio.com/about",
    description:
      "Learn about Parish Audio, an independent audio software company creating plugins for music production, mixing, sound design, and creative tone shaping.",
    mainEntity: {
      "@type": "Organization",
      name: "Parish Audio",
      url: "https://parishaudio.com",
      description:
        "Parish Audio creates audio plugins for music production, mixing, sound design, and creative tone shaping.",
    },
  };

  return (
    <>
      <Seo
        title="About | Parish Audio"
        description="Learn about Parish Audio, an independent audio software company creating plugins for music production, mixing, sound design, and creative tone shaping."
        canonical="https://parishaudio.com/about"
        schema={aboutSchema}
      />

      <Box sx={{ width: "95%", maxWidth: 1000, mx: "auto", minHeight: height - 140 }}>
        <Paper
          sx={{
            mt: 3,
            mb: 4,
            overflow: "hidden",
            borderRadius: 3,
            backgroundColor: style.color1,
            color: style.color4,
            boxShadow: 10,
          }}
        >
          <Box
            ref={topRef}
            sx={{
              px: { xs: 2, md: 5 },
              py: { xs: 4, md: 6 },
              background:
                "linear-gradient(180deg, rgba(255,255,255,0.04) 0%, rgba(0,0,0,0.10) 100%)",
            }}
          >
            <Typography
              variant="h1"
              sx={{
                fontSize: { xs: "2rem", md: "3.2rem" },
                fontWeight: 800,
                lineHeight: 1.05,
                color: style.color4,
                maxWidth: 700,
              }}
            >
              Audio tools built for real music-making
            </Typography>

            <Typography
              sx={{
                mt: 2,
                fontSize: { xs: "1rem", md: "1.1rem" },
                lineHeight: 1.9,
                color: style.color3,
                maxWidth: 760,
              }}
            >
              Parish Audio creates plugins for music production, mixing, sound
              design, and tone shaping. The goal is simple: make tools that sound
              inspiring, feel intuitive, and help creators work faster without
              getting in the way of the music.
            </Typography>
          </Box>

          <Box sx={{ px: { xs: 2, md: 5 }, py: { xs: 3, md: 4 } }}>
            <Grid container spacing={4} alignItems="start">
              <Grid item xs={12} md={6}>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: "1.35rem",
                    fontWeight: 800,
                    mb: 1.5,
                    color: style.color4,
                  }}
                >
                  What Parish Audio is about
                </Typography>

                <Stack spacing={2}>
                  <Typography sx={{ color: style.color3, lineHeight: 1.85 }}>
                    Parish Audio is focused on making plugins that are musical,
                    practical, and enjoyable to use. Every tool should earn its
                    place by helping the creative process, whether that means
                    shaping tone, adding depth, controlling dynamics, or making a
                    mix come together more naturally.
                  </Typography>

                  <Typography sx={{ color: style.color3, lineHeight: 1.85 }}>
                    Some plugins are designed to be fast and straightforward.
                    Others invite more experimentation. In both cases, the aim is
                    the same: deliver strong sound, clear controls, and a workflow
                    that feels natural.
                  </Typography>

                  <Typography sx={{ color: style.color3, lineHeight: 1.85 }}>
                    Parish Audio products are built for musicians, producers, and
                    engineers who want software that feels creative instead of
                    clinical.
                  </Typography>
                </Stack>
              </Grid>

              <Grid item xs={12} md={6}>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: "1.35rem",
                    fontWeight: 800,
                    mb: 1.5,
                    color: style.color4,
                  }}
                >
                  What you will find here
                </Typography>

                <Box
                  component="ul"
                  sx={{
                    pl: 2.5,
                    mt: 0,
                    mb: 0,
                    color: style.color3,
                    "& li": {
                      mb: 1.4,
                      lineHeight: 1.8,
                    },
                  }}
                >
                  <li>Downloadable plugins for Mac and Windows</li>
                  <li>Tools for mixing, production, and creative sound design</li>
                  <li>Products designed with an emphasis on tone and usability</li>
                  <li>Educational content in the Parish Audio Learning Center</li>
                  <li>Ongoing updates and new releases over time</li>
                </Box>
              </Grid>
            </Grid>
          </Box>

          <Divider sx={{ backgroundColor: "rgba(255,255,255,0.08)" }} />

          <Box sx={{ px: { xs: 2, md: 5 }, py: { xs: 3, md: 4 } }}>
            <Typography
              variant="h2"
              sx={{
                fontSize: "1.35rem",
                fontWeight: 800,
                mb: 2,
                color: style.color4,
              }}
            >
              Core values
            </Typography>

            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Box>
                  <Typography sx={{ fontWeight: 700, color: style.color4, mb: 0.5 }}>
                    Great sound first
                  </Typography>
                  <Typography sx={{ color: style.color3, lineHeight: 1.75 }}>
                    The plugin should sound musical and useful before anything else.
                  </Typography>
                </Box>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Box>
                  <Typography sx={{ fontWeight: 700, color: style.color4, mb: 0.5 }}>
                    Clear, simple controls
                  </Typography>
                  <Typography sx={{ color: style.color3, lineHeight: 1.75 }}>
                    A good interface should support creativity, not slow it down.
                  </Typography>
                </Box>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Box>
                  <Typography sx={{ fontWeight: 700, color: style.color4, mb: 0.5 }}>
                    Creative flexibility
                  </Typography>
                  <Typography sx={{ color: style.color3, lineHeight: 1.75 }}>
                    Tools should work for writing, recording, mixing, and experimenting.
                  </Typography>
                </Box>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Box>
                  <Typography sx={{ fontWeight: 700, color: style.color4, mb: 0.5 }}>
                    Independent development
                  </Typography>
                  <Typography sx={{ color: style.color3, lineHeight: 1.75 }}>
                    Products are shaped through hands-on design, testing, and refinement.
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          </Box>

          <Divider sx={{ backgroundColor: "rgba(255,255,255,0.08)" }} />

          <Box sx={{ px: { xs: 2, md: 5 }, py: { xs: 3, md: 4 } }}>
            <Stack
              direction={{ xs: "column", md: "row" }}
              spacing={2}
              alignItems={{ xs: "flex-start", md: "center" }}
              justifyContent="space-between"
            >
              <Box sx={{ maxWidth: 620 }}>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: "1.35rem",
                    fontWeight: 800,
                    mb: 1,
                    color: style.color4,
                  }}
                >
                  Explore Parish Audio
                </Typography>

                <Typography sx={{ color: style.color3, lineHeight: 1.8 }}>
                  Browse the plugin lineup, visit the Learning Center, or get in
                  touch if you have questions about products, compatibility, or support.
                </Typography>
              </Box>

              <Stack direction={{ xs: "column", sm: "row" }} spacing={1.25}>
                <Button
                  component={RouterLink}
                  to="/plugins"
                  variant="contained"
                  sx={{
                    backgroundColor: style.color2,
                    color: style.color4,
                    fontWeight: 700,
                    px: 2.5,
                    py: 1.1,
                    minWidth: 170,
                    "&:hover": {
                      filter: "brightness(1.1)",
                      backgroundColor: style.color2,
                    },
                  }}
                >
                  Browse Plugins
                </Button>

                <Button
                  component={RouterLink}
                  to="/learning"
                  variant="outlined"
                  sx={{
                    borderColor: style.color2,
                    color: style.color4,
                    fontWeight: 700,
                    px: 2.5,
                    py: 1.1,
                    minWidth: 170,
                    "&:hover": {
                      borderColor: style.color4,
                      backgroundColor: "rgba(255,255,255,0.04)",
                    },
                  }}
                >
                  Learning Center
                </Button>
              </Stack>
            </Stack>
          </Box>
        </Paper>
      </Box>
    </>
  );
}